(() => {
  'use strict';

  const roots = document.querySelectorAll(
    '[data-f2w-gyms]'
  );

  if (!roots.length) {
    return;
  }

  const normalize = (value) => {
    return String(value || '')
      .toLocaleLowerCase('nl-NL')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const uniqueSorted = (values) => {
    const result = new Map();

    values
      .flatMap((value) => {
        return Array.isArray(value)
          ? value
          : [value];
      })
      .map((value) => {
        return String(value || '').trim();
      })
      .filter(Boolean)
      .forEach((value) => {
        const key = normalize(value);

        if (!result.has(key)) {
          result.set(key, value);
        }
      });

    return [...result.values()].sort((a, b) => {
      return a.localeCompare(b, 'nl-NL');
    });
  };

  const referenceLabel = (reference) => {
    return String(
      reference?.name?.value ||
      reference?.handle ||
      ''
    ).trim();
  };

  const nativeUrl = (value) => {
    if (!value) {
      return '';
    }

    try {
      const url = new URL(
        String(value),
        window.location.origin
      );

      if (
        url.protocol !== 'http:' &&
        url.protocol !== 'https:'
      ) {
        return '';
      }

      const root =
        window.Shopify?.routes?.root || '/';

      const prefix =
        root === '/'
          ? ''
          : root.replace(/\/$/, '');

      let path = url.pathname;

      if (/^\/[a-z]{2}(\/|$)/i.test(path)) {
        path = path.replace(/^\/[a-z]{2}/i, '');
      }

      if (
        prefix &&
        path.startsWith(`${prefix}/`)
      ) {
        path = path.slice(prefix.length);
      }

      return `${prefix}${path}${url.search}${url.hash}`;
    } catch (error) {
      return '';
    }
  };

  const countryFlagElement = (
    code,
    label
  ) => {
    const flagCode = String(code || '')
      .trim()
      .toLowerCase();

    const isNation =
      flagCode === 'gb-sct' ||
      flagCode === 'gb-eng' ||
      flagCode === 'gb-wls';

    if (
      !flagCode ||
      (flagCode.length !== 2 && !isNation)
    ) {
      return null;
    }

    const wrapper =
      document.createElement('span');
    wrapper.className = 'f2w-country-flag';

    const image =
      document.createElement('img');
    image.src =
      `https://flagcdn.com/w40/${flagCode}.png`;
    image.srcset =
      `https://flagcdn.com/w80/${flagCode}.png 2x`;
    image.alt = String(label || '');
    image.width = 20;
    image.height = 15;
    image.loading = 'lazy';
    image.decoding = 'async';

    wrapper.appendChild(image);
    wrapper.appendChild(
      document.createTextNode('\u00a0')
    );

    return wrapper;
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);

    if (className) {
      node.className = className;
    }

    if (
      text !== undefined &&
      text !== null
    ) {
      node.textContent = text;
    }

    return node;
  };

  // Content query for one chunk of locations. Images come from
  // `resolved_image_url`, a plain url-type field (not a file reference),
  // so the Storefront API reads it directly like any other string - no
  // reference-resolution problem, no Liquid image-map pass, no 1,000-item
  // paginate cap to worry about as this list grows. Paste the CDN URL of
  // a location's uploaded image into that field to give it a card image.
  const METAOBJECT_LABELS_QUERY = `
    query F2WGymLabels(
      $type: String!
      $cursor: String
      $language: LanguageCode!
    ) @inContext(language: $language) {
      metaobjects(type: $type, first: 250, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes {
          id
          handle
          name: field(key: "name") { value }
          code: field(key: "code") { value }
        }
      }
    }
  `;

  const CITY_REGIONS_QUERY = `
    query F2WGymCityRegions($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          country: field(key: "country") { value }
          region: field(key: "region") {
            reference { ... on Metaobject {
              id
              name: field(key: "name") { value }
              country: field(key: "country") { value }
            } }
          }
        }
      }
    }
  `;

  const NODES_QUERY = `
    query F2WGymsByIds(
      $ids: [ID!]!
      $language: LanguageCode!
    ) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          handle
          onlineStoreUrl
          name: field(key: "name") { value }
          city: field(key: "city") { value }
          resolved_image_url: field(key: "resolved_image_url") { value }
          country: field(key: "country") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
                code: field(key: "code") { value }
              }
            }
          }
          sports: field(key: "sports") {
            references(first: 20) {
              nodes {
                ... on Metaobject {
                  handle
                  name: field(key: "name") { value }
                }
              }
            }
          }
          amenities: field(key: "amenities") {
            references(first: 30) {
              nodes {
                ... on Metaobject {
                  handle
                  name: field(key: "name") { value }
                  category: field(key: "category") { value }
                }
              }
            }
          }
        }
      }
    }
  `;

  const chunk = (values, size) => {
    const chunks = [];
    for (let index = 0; index < values.length; index += size) {
      chunks.push(values.slice(index, index + size));
    }
    return chunks;
  };

  roots.forEach((root) => {
    if (root.dataset.f2wInitialized === 'true') return;
    root.dataset.f2wInitialized = 'true';
    root.hidden = false;

    const token = root.dataset.storefrontToken || '';
    const shop = root.dataset.shopDomain || window.Shopify?.shop || window.location.hostname;
    const apiVersion = root.dataset.apiVersion || '2024-10';
    const endpoint = root.dataset.storefrontEndpoint || `https://${shop}/api/${apiVersion}/graphql.json`;
    const indexUrl = root.dataset.indexUrl || '';
    const language = String(root.dataset.language || document.documentElement?.lang || 'nl')
      .split('-')[0].trim().toUpperCase() || 'NL';
    const perPage = Math.max(1, parseInt(root.dataset.perPage, 10) || 48);
    const countFormatter = new Intl.NumberFormat(root.dataset.language || document.documentElement?.lang || 'nl');
    root.querySelectorAll('[data-f2w-gym-snapshot-value]').forEach((value) => {
      const number = Number(value.textContent.trim());
      if (Number.isFinite(number)) value.textContent = countFormatter.format(number);
    });

    const searchInput = root.querySelector('[data-f2w-gyms-filter-search]');
    const searchFilter = root.querySelector('[data-f2w-gyms-search-filter]') ||
      searchInput?.closest('.f2w-event-directory__filter');
    const countrySelect = root.querySelector('[data-f2w-gyms-filter-country]');
    const citySelect = root.querySelector('[data-f2w-gyms-filter-city]');
    const regionSelect = root.querySelector('[data-f2w-gyms-filter-region]');
    const regionThreshold = Math.max(1, Number(root.dataset.regionCityThreshold) || 20);
    const cityRegions = new Map();
    let regionRequestId = 0;
    const sportSelect = root.querySelector('[data-f2w-gyms-filter-sport]');
    const audienceSelect = root.querySelector('[data-f2w-gyms-filter-audience]');
    const filterContainer = root.querySelector('[data-f2w-directory-filters]');
    const picker = Object.fromEntries(['country', 'sport', 'region', 'city'].map((kind) => [kind, {
      wrap: root.querySelector('[data-f2w-gym-picker="' + kind + '"]'),
      quick: root.querySelector('[data-f2w-gym-quick="' + kind + '"]'),
      more: root.querySelector('[data-f2w-gym-more="' + kind + '"]'),
      panel: root.querySelector('[data-f2w-gym-panel="' + kind + '"]'),
      list: root.querySelector('[data-f2w-gym-list="' + kind + '"]')
    }]));
    const expanded = { country: false };
    let sportChosen = false;
    const scopeFilters = [...root.querySelectorAll('[data-f2w-gyms-scope-filter]')];
    const loadedFilter = root.querySelector('[data-f2w-gyms-loaded-filter]');
    const resultCount = root.querySelector('[data-f2w-gyms-results-count]');
    const loadingElement = root.querySelector('[data-f2w-gyms-loading]');
    const errorElement = root.querySelector('[data-f2w-gyms-error]');
    const emptyElement = root.querySelector('[data-f2w-gyms-empty]');
    const resultsGrid = root.querySelector('[data-f2w-gyms-results]');
    const pagination = root.querySelector('[data-f2w-gyms-pagination]');

    const labels = {
      results: root.dataset.labelResults || '',
      available: root.dataset.labelAvailable || '',
      selectCountry: root.dataset.labelSelectCountry || '',
      loading: root.dataset.labelLoading || '',
      loadingSelection: root.dataset.labelLoadingSelection || '',
      error: root.dataset.labelError || '',
      empty: root.dataset.labelEmpty || '',
      pagination: root.dataset.labelPagination || '',
      previous: root.dataset.labelPrevious || '',
      next: root.dataset.labelNext || '',
      view: root.dataset.labelView || '',
      types: {
        gym: root.dataset.labelTypeGym || '',
        school: root.dataset.labelTypeSchool || '',
        dojo: root.dataset.labelTypeDojo || '',
        dojang: root.dataset.labelTypeDojang || ''
      }
    };

    let indexRecords = [];
    let countryLabels = new Map();
    let sportLabels = new Map();
    const allCountriesLabel =
      countrySelect?.options?.[0]?.textContent || '';
    let gyms = [];
    let currentPage = 1;
    let loadedSelectionKey = '';
    let loadError = false;
    let ready = false;
    const selectionCache = new Map();
    let selectionRequestId = 0;

    const setSearchVisibility = (visible) => {
      if (!searchFilter) return;
      searchFilter.hidden = !visible;
      searchFilter.style.display = visible ? '' : 'none';
    };

    const setElementVisibility = (element, visible) => {
      if (!element) return;
      element.hidden = !visible;
      element.style.display = visible ? '' : 'none';
      element.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    const chipButton = (kind, value, label, select) => {
      const button = element('button', 'f2w-event-card__sport', label);
      button.type = 'button';
      if (kind === 'country' && value && value !== '__all__') {
        const option = [...countrySelect.options].find((entry) => entry.value === value);
        const flag = countryFlagElement(option?.dataset.code, label);
        if (flag) {
          flag.setAttribute('aria-hidden', 'true');
          const image = flag.querySelector('img');
          if (image) {
            image.alt = '';
            image.addEventListener('error', () => flag.remove(), { once: true });
          }
          button.prepend(flag);
        }
      }
      button.setAttribute('aria-pressed', String((kind !== 'sport' || sportChosen) && select.value === value));
      button.addEventListener('click', () => {
        if (kind === 'country') {
          sportChosen = false;
          expanded.country = value === '__all__';
          picker.country.more.setAttribute('aria-expanded', String(expanded.country));
          picker.country.more.textContent = expanded.country ? root.dataset.lessCountries : root.dataset.moreCountries;
        }
        if (kind === 'sport') { sportChosen = true; citySelect.value = ''; if (regionSelect) regionSelect.value = ''; }
        if (kind === 'region') citySelect.value = '';
        select.value = value;
        if (picker[kind].panel) picker[kind].panel.hidden = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        picker[kind].quick.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
      });
      return button;
    };
    const syncCountryChips = () => {
      if (!ready || !countrySelect) return;
      const allOptions = [...countrySelect.options].slice(2);
      const preference = String(root.dataset.countryChipCodes || '').split(',').map((code) => code.trim().toUpperCase());
      const counts = new Map();
      indexRecords.forEach((record) => counts.set(record.countryId, (counts.get(record.countryId) || 0) + 1));
      const ranked = allOptions.sort((a, b) => {
        const aCode = String(a.dataset.code || '').toUpperCase(), bCode = String(b.dataset.code || '').toUpperCase();
        const aRank = ['NL','BE'].indexOf(aCode), bRank = ['NL','BE'].indexOf(bCode);
        if (aRank !== bRank && (aRank >= 0 || bRank >= 0)) return aRank < 0 ? 1 : bRank < 0 ? -1 : aRank - bRank;
        const aPref = preference.indexOf(aCode), bPref = preference.indexOf(bCode);
        if (aPref !== bPref && (aPref >= 0 || bPref >= 0)) return aPref < 0 ? 1 : bPref < 0 ? -1 : aPref - bPref;
        return (counts.get(b.value) || 0) - (counts.get(a.value) || 0) || a.textContent.localeCompare(b.textContent, language);
      });
      const featured = ranked.slice(0, 6);
      const selected = ranked.find((option) => option.value === countrySelect.value);
      if (selected && !featured.includes(selected)) { featured.pop(); featured.push(selected); }
      const country = picker.country;
      const allChip = () => chipButton('country', '__all__', countrySelect.options[1].textContent, countrySelect);
      const visibleCountries = selected ? [selected] : featured;
      country.quick.replaceChildren(allChip(), ...visibleCountries.map((option) => chipButton('country', option.value, option.textContent, countrySelect)),
        ...(expanded.country ? [country.panel, country.more] : [country.more, country.panel]));
      country.list.replaceChildren(
        ...ranked.filter((option) => !visibleCountries.includes(option)).sort((a, b) => a.textContent.localeCompare(b.textContent, language))
          .map((option) => chipButton('country', option.value, option.textContent, countrySelect)));
      const canExpandCountries = !selected && ranked.length > 6;
      country.more.hidden = !canExpandCountries || expanded.country;
      country.panel.hidden = !canExpandCountries || !expanded.country;
      country.more.textContent = root.dataset.moreCountries;
      if (picker.region.wrap && regionSelect) {
        const visible = regionFilterAvailable();
        picker.region.wrap.hidden = !visible;
        picker.region.quick.replaceChildren(...(visible ? [...regionSelect.options].map((option) =>
          chipButton('region', option.value, option.textContent, regionSelect)) : []));
      }
      for (const kind of ['sport','city']) {
        const select = kind === 'sport' ? sportSelect : citySelect;
        const controls = picker[kind];
        controls.wrap.hidden = !countrySelect.value || (kind === 'city' && !sportChosen);
        if (controls.wrap.hidden) { controls.quick.replaceChildren(); continue; }
        const relevant = countryIndexRecords().filter((record) => kind === 'sport'
          || ((!sportSelect.value || record.sports.includes(sportSelect.value)) &&
              (!regionSelect?.value || regionFor(record)?.id === regionSelect.value)));
        const counts = new Map();
        relevant.forEach((record) => {
          const keys = kind === 'sport' ? record.sports : [normalize(record.city)];
          keys.forEach((key) => { if (key) counts.set(key, (counts.get(key) || 0) + 1); });
        });
        const options = [...select.options].slice(1).sort((a, b) => (counts.get(b.value) || 0) - (counts.get(a.value) || 0)
          || a.textContent.localeCompare(b.textContent, language));
        if (kind === 'city') {
          const selected = options.find((option) => option.value === select.value);
          const allCities = chipButton('city', '', select.options[0].textContent, select);
          controls.quick.replaceChildren(
            allCities,
            ...(selected ? [chipButton('city', selected.value, selected.textContent, select)] :
              options.map((option) => chipButton('city', option.value, option.textContent, select)))
          );
          continue;
        }
        const top = options.slice(0, 6);
        const rest = options.filter((option) => !top.includes(option))
          .sort((a, b) => a.textContent.localeCompare(b.textContent, language));
        controls.quick.replaceChildren(chipButton(kind, '', select.options[0].textContent, select),
          ...[...top, ...rest].map((option) => chipButton(kind, option.value, option.textContent, select)));
      }
      root.dataset.f2wGymChipsReady = 'true';
    };
    const countryMore = picker.country.more;
    const countryPanel = picker.country.panel;
    countryMore.addEventListener('click', () => {
      expanded.country = !expanded.country;
      countryMore.setAttribute('aria-expanded', String(expanded.country));
      countryMore.textContent = expanded.country ? root.dataset.lessCountries : root.dataset.moreCountries;
      syncCountryChips();
      if (expanded.country) picker.country.list.querySelector('button')?.focus({ preventScroll: true });
    });
    countryPanel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      expanded.country = false;
      countryMore.setAttribute('aria-expanded', 'false');
      countryMore.textContent = root.dataset.moreCountries;
      syncCountryChips();
      countryMore.focus();
    });
    const updateFilterVisibility = () => {
      const hasCountry = Boolean(countrySelect?.value);
      const hasLoadedSelection = Boolean(loadedSelectionKey);
      scopeFilters.forEach((filter) => setElementVisibility(filter, hasCountry));
      setElementVisibility(loadedFilter, false);
      const activeQuery = Boolean(searchInput?.value.trim());
      const showSearch = hasLoadedSelection && (sportChosen || activeQuery) &&
        (filteredGyms(true).length > 48 || activeQuery);
      setSearchVisibility(showSearch);
      if (filterContainer) {
        filterContainer.hidden = !showSearch;
        if (showSearch) filterContainer.style.removeProperty('display');
        else filterContainer.style.setProperty('display', 'none', 'important');
      }
    };

    setSearchVisibility(false);
    updateFilterVisibility();

    const requestQuery = async (query, variables) => {
      if (!token) throw new Error('Missing Storefront token');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query, variables })
      });
      if (!response.ok) throw new Error('Storefront request failed');
      const json = await response.json();
      if (Array.isArray(json.errors) && json.errors.length) {
        throw new Error('Storefront query failed');
      }
      return json.data || {};
    };

    const loadLabels = async (type) => {
      const nodes = [];
      let cursor = null;
      do {
        const data = await requestQuery(METAOBJECT_LABELS_QUERY, {
          type,
          cursor,
          language
        });
        const page = data.metaobjects;
        if (!page) throw new Error(`No ${type} labels returned`);
        nodes.push(...(page.nodes || []));
        cursor = page.pageInfo?.hasNextPage ? page.pageInfo.endCursor : null;
        if (page.pageInfo?.hasNextPage && !cursor) throw new Error(`Missing ${type} cursor`);
      } while (cursor);
      return nodes;
    };

    const parseIndex = (payload) => {
      if (!payload || payload.v !== 1 || !payload.countries) {
        throw new Error('Invalid gym index');
      }
      const records = [];
      Object.entries(payload.countries).forEach(([countryId, bucket]) => {
        (bucket.r || []).forEach((row) => {
          records.push({
            id: row[0],
            locationType: row[1],
            cityId: row[2] || '',
            city: String(row[3] || '').trim(),
            sports: Array.isArray(row[4]) ? row[4] : [],
            countryId: countryId === '__missing_country__' ? '' : countryId,
            countryCode: String(bucket.c || '').trim().toUpperCase()
          });
        });
      });
      if (records.length !== payload.count) throw new Error('Gym index count mismatch');
      return records;
    };

    const parsePublishedIndex = (payload) => {
      if (!Array.isArray(payload)) return parseIndex(payload);
      const joined = { v: 1, count: null, countries: Object.create(null) };
      for (const part of payload) {
        if (!part || part.v !== 1 || !Number.isInteger(part.count) || part.count < 0 ||
            !part.countries || typeof part.countries !== 'object' || Array.isArray(part.countries)) {
          throw new Error('Invalid published Gym part');
        }
        if (joined.count !== null && joined.count !== part.count) {
          throw new Error('Conflicting published Gym counts');
        }
        joined.count = part.count;
        for (const [countryId, bucket] of Object.entries(part.countries)) {
          if (!bucket || typeof bucket.c !== 'string' || !Array.isArray(bucket.r)) {
            throw new Error('Invalid published Gym country');
          }
          if (!joined.countries[countryId]) joined.countries[countryId] = { c: bucket.c, r: [] };
          const current = joined.countries[countryId];
          if (current.c && bucket.c && current.c !== bucket.c) {
            throw new Error('Conflicting published Gym country codes');
          }
          if (!current.c) current.c = bucket.c;
          current.r.push(...bucket.r);
        }
      }
      if (joined.count === null) throw new Error('Empty published Gym index');
      return parseIndex(joined);
    };

    const loadIndex = async () => {
      const published = root.querySelector('[data-f2w-gym-index]');
      if (published) {
        try {
          return parsePublishedIndex(JSON.parse(published.textContent || 'null'));
        } catch (error) {
          console.warn('FightBase Gyms: published index unavailable; using theme asset.', error);
        }
      }
      if (!indexUrl) throw new Error('Missing gym index URL');
      const response = await fetch(indexUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Gym index request failed');
      return parseIndex(await response.json());
    };

    const typeLabel = (type) => labels.types[type] || type;

    const mapGym = (node, indexRecord) => {
      if (!node || !indexRecord) return null;
      const sports = node.sports?.references?.nodes || [];
      const audiences = (node.amenities?.references?.nodes || [])
        .filter((amenity) => normalize(amenity?.category?.value) === 'audience')
        .map((amenity) => ({
          handle: String(amenity.handle || '').trim(),
          label: String(amenity.name?.value || '').trim()
        }))
        .filter((audience) => audience.handle && audience.label);
      const country = node.country?.reference;
      return {
        id: node.id || '',
        handle: node.handle || '',
        url: nativeUrl(node.onlineStoreUrl),
        name: String(node.name?.value || '').trim(),
        cityId: indexRecord.cityId,
        locationType: indexRecord.locationType,
        locationTypeLabel: typeLabel(indexRecord.locationType),
        city: String(node.city?.value || indexRecord.city || '').trim(),
        countryId: country?.id || indexRecord.countryId,
        country: country ? referenceLabel(country) : (countryLabels.get(indexRecord.countryId)?.name || ''),
        countryCode: String(country?.code?.value || indexRecord.countryCode || '').trim(),
        sports: uniqueSorted(sports.map(referenceLabel).filter(Boolean)),
        sportHandles: sports.map((sport) => String(sport.handle || '').trim()).filter(Boolean),
        audiences,
        image: String(node.resolved_image_url?.value || '').trim()
      };
    };
    const createCard = (gym) => {
      const article = element(
        'article',
        'f2w-event-card'
      );

      article.dataset.f2wGymCard = '';

      const card = gym.url
        ? element(
            'a',
            'f2w-event-card__link'
          )
        : element(
            'div',
            'f2w-event-card__link'
          );

      if (gym.url) {
        card.href = gym.url;
      }

      const imageWrapper = element(
        'div',
        'f2w-event-card__image'
      );

      if (gym.image) {
        const image = document.createElement(
          'img'
        );

        image.src = gym.image;
        image.alt = gym.name;
        image.width = 1100;
        image.height = 825;
        image.loading = 'lazy';

        imageWrapper.appendChild(image);
      } else {
        const placeholder = element(
          'div',
          'f2w-event-card__image--placeholder'
        );

        placeholder.appendChild(
          element(
            'span',
            '',
            gym.name.slice(0, 1)
          )
        );

        imageWrapper.appendChild(
          placeholder
        );
      }

      const body = element(
        'div',
        'f2w-event-card__body'
      );

      const content = element(
        'div',
        'f2w-event-card__content'
      );

      if (gym.locationTypeLabel) {
        const topline = element(
          'div',
          'f2w-event-card__topline'
        );

        topline.appendChild(
          element(
            'span',
            'f2w-event-card__period f2w-event-card__period--upcoming',
            gym.locationTypeLabel
          )
        );

        content.appendChild(topline);
      }

      content.appendChild(
        element(
          'h2',
          'f2w-event-card__title',
          gym.name
        )
      );

      if (gym.city) {
        content.appendChild(
          element(
            'p',
            'f2w-event-card__location',
            gym.city
          )
        );
      }

      if (gym.country) {
        const countryElement = element(
          'p',
          'f2w-event-card__location',
          gym.country
        );

        const flagElement =
          countryFlagElement(
            gym.countryCode,
            gym.country
          );

        if (flagElement) {
          countryElement.insertBefore(
            flagElement,
            countryElement.firstChild
          );
        }

        content.appendChild(
          countryElement
        );
      }

      if (gym.sports.length) {
        const sports = element(
          'div',
          'f2w-event-card__sports'
        );

        gym.sports.forEach((sport) => {
          sports.appendChild(
            element(
              'span',
              'f2w-event-card__sport',
              sport
            )
          );
        });

        content.appendChild(sports);
      }

      if (
        gym.url &&
        labels.view
      ) {
        content.appendChild(
          element(
            'span',
            'f2w-event-card__view',
            labels.view
          )
        );
      }

      body.appendChild(content);
      card.appendChild(imageWrapper);
      card.appendChild(body);
      article.appendChild(card);

      return article;
    };


    const countryIndexRecords = () => {
      const countryId = countrySelect?.value || '';
      if (!countryId) return [];
      return indexRecords.filter((record) => {
        return countryId === '__all__' || record.countryId === countryId;
      });
    };

    const sportIndexRecords = () => countryIndexRecords().filter((record) =>
      !sportSelect?.value || record.sports.includes(sportSelect.value));
    const regionFor = (record) => {
      const city = cityRegions.get(record.cityId);
      return city && city.countryId === record.countryId &&
        (!city.regionCountryId || city.regionCountryId === record.countryId)
        ? city : null;
    };
    const regionFilterAvailable = () => {
      if (!regionSelect || !sportChosen || !countrySelect.value || countrySelect.value === '__all__') return false;
      const records = sportIndexRecords();
      const cities = new Set(records.map((record) => record.cityId || normalize(record.city)).filter(Boolean));
      const regions = new Set(records.map((record) => regionFor(record)?.id).filter(Boolean));
      return cities.size > regionThreshold && regions.size > 1;
    };
    const loadCountryRegions = async () => {
      const requestId = ++regionRequestId;
      if (!regionSelect || !countrySelect.value || countrySelect.value === '__all__') return;
      const records = countryIndexRecords();
      const cities = new Set(records.map((record) => record.cityId || normalize(record.city)).filter(Boolean));
      if (cities.size <= regionThreshold) return;
      const ids = [...new Set(records.map((record) => record.cityId).filter(Boolean))]
        .filter((id) => !cityRegions.has(id));
      const pendingRegions = new Map();
      try {
        for (const batch of chunk(ids, 250)) {
          if (requestId !== regionRequestId) return;
          const data = await requestQuery(CITY_REGIONS_QUERY, { ids: batch, language });
          if (!Array.isArray(data.nodes) || data.nodes.length !== batch.length) throw new Error('Incomplete city regions');
          data.nodes.forEach((city, index) => {
            const region = city?.region?.reference;
            if (city && city.id !== batch[index]) throw new Error('Mismatched city reference');
            pendingRegions.set(batch[index], region?.id && region.name?.value ? {
              id: region.id, label: region.name.value,
              countryId: city.country?.value || '', regionCountryId: region.country?.value || ''
            } : null);
          });
        }
      } catch (error) {
        console.warn('FightBase Gyms: region refinement unavailable.', error);
        return;
      }
      if (requestId !== regionRequestId) return;
      pendingRegions.forEach((value, id) => cityRegions.set(id, value));
      rebuildScopeFilters();
      if (loadedSelectionKey) renderArchive();
    };

    const selectedIndexRecords = () => {
      const city = citySelect?.value || '';
      const sport = sportSelect?.value || '';
      return countryIndexRecords().filter((record) => {
        return (!regionSelect?.value || regionFor(record)?.id === regionSelect.value) &&
          (!city || normalize(record.city) === city) &&
          (!sport || record.sports.includes(sport));
      });
    };

    const currentFilters = () => ({
      search: searchInput?.value || '',
      country: countrySelect?.value || '',
      region: regionSelect?.value || '',
      city: citySelect?.value || '',
      sport: sportSelect?.value || '',
      audience: audienceSelect?.value || ''
    });

    const filteredGyms = (ignoreSearch = false) => {
      const filters = currentFilters();
      const search = ignoreSearch ? '' : normalize(filters.search);
      return gyms.filter((gym) => {
        const searchText = normalize([
          gym.name,
          gym.city,
          gym.country,
          gym.locationTypeLabel,
          ...gym.sports,
          ...gym.audiences.map((item) => item.label)
        ].join(' '));
        return (!search || searchText.includes(search)) &&
          (!filters.country || filters.country === '__all__' || gym.countryId === filters.country) &&
          (!filters.region || regionFor({ cityId: gym.cityId, countryId: gym.countryId })?.id === filters.region) &&
          (!filters.city || normalize(gym.city) === filters.city) &&
          (!filters.sport || gym.sportHandles.includes(filters.sport)) &&
          (!filters.audience || gym.audiences.some((item) => item.handle === filters.audience));
      });
    };

    const selectedCountryCode = () => {
      const option = countrySelect?.selectedOptions?.[0];
      return String(option?.dataset?.code || '').trim();
    };

    const updateUrl = () => {
      const url = new URL(window.location.href);
      const filters = currentFilters();
      const values = {
        q: filters.search.trim(),
        country: selectedCountryCode(),
        region: filters.region,
        city: filters.city,
        sport: filters.sport,
        audience: filters.audience,
        page: currentPage > 1 ? String(currentPage) : ''
      };
      Object.entries(values).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
        else url.searchParams.delete(key);
      });
      window.history.replaceState(window.history.state || {}, '', url);
    };

    const renderArchive = () => {
      if (!resultsGrid) return;
      const filtered = filteredGyms();
      updateFilterVisibility();
      const totalPages = Math.ceil(filtered.length / perPage);
      currentPage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));
      const visible = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
      resultsGrid.replaceChildren();
      visible.forEach((gym) => resultsGrid.appendChild(createCard(gym)));
      if (resultCount) {
        resultCount.textContent = loadedSelectionKey
          ? [countFormatter.format(filtered.length), labels.results].filter(Boolean).join(' ')
          : '';
      }
      if (emptyElement) emptyElement.hidden = !loadedSelectionKey || filtered.length !== 0;
      if (pagination) {
        pagination.hidden = totalPages <= 1;
        const controls = [];
        const pageButton = (text, target, ariaLabel, active) => {
          const button = element('button', 'f2w-event-directory__tab', text);
          button.type = 'button';
          if (ariaLabel) button.setAttribute('aria-label', ariaLabel);
          if (active) button.setAttribute('aria-current', 'page');
          else button.addEventListener('click', () => {
            currentPage = target;
            renderArchive();
            pagination.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
            resultsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
          return button;
        };
        if (totalPages > 1) {
          if (currentPage > 1) controls.push(pageButton('◀', currentPage - 1, labels.previous, false));
          const pages = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages]
            .filter((number) => number >= 1 && number <= totalPages))].sort((a, b) => a - b);
          let previous = 0;
          for (const number of pages) {
            if (number - previous > 1) controls.push(element('span', '', '…'));
            controls.push(pageButton(String(number), number, `${labels.pagination} ${number}`, number === currentPage));
            previous = number;
          }
          if (currentPage < totalPages) controls.push(pageButton('▶', currentPage + 1, labels.next, false));
        }
        pagination.replaceChildren(...controls);
      }
      updateUrl();
    };

    const rebuildSimpleSelect = (select, options, firstLabel) => {
      if (!select) return;
      const current = select.value;
      select.replaceChildren();
      const first = document.createElement('option');
      first.value = '';
      first.textContent = firstLabel;
      select.appendChild(first);
      options.forEach(({ value, label, code }) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        if (code) option.dataset.code = code;
        select.appendChild(option);
      });
      select.value = [...select.options].some((option) => option.value === current) ? current : '';
    };

    const rebuildCountrySelect = () => {
      const ids = [...new Set(indexRecords.map((record) => record.countryId).filter(Boolean))];
      const options = ids.map((id) => {
        const meta = countryLabels.get(id) || {};
        const record = indexRecords.find((item) => item.countryId === id);
        return { value: id, label: meta.name || record?.countryCode || id, code: meta.code || record?.countryCode || '' };
      }).sort((a, b) => a.label.localeCompare(b.label, document.documentElement.lang || 'nl'));
      rebuildSimpleSelect(
        countrySelect,
        [{ value: '__all__', label: allCountriesLabel, code: 'ALL' }, ...options],
        labels.selectCountry
      );
    };

    const rebuildScopeFilters = () => {
      const countryId = countrySelect?.value || '';
      const countryRecords = indexRecords.filter((record) => {
        return countryId === '__all__' || record.countryId === countryId;
      });
      const cityFirst = citySelect?.options?.[0]?.textContent || '';
      const sportFirst = sportSelect?.options?.[0]?.textContent || '';
      const selectedSport = sportSelect?.value || '';
      let cityRecords = selectedSport
        ? countryRecords.filter((record) => record.sports.includes(selectedSport))
        : countryRecords;
      if (regionSelect) {
        const regions = new Map();
        cityRecords.forEach((record) => {
          const region = regionFor(record);
          if (region) regions.set(region.id, region.label);
        });
        const showRegions = regionFilterAvailable();
        if (!showRegions) regionSelect.value = '';
        rebuildSimpleSelect(regionSelect, showRegions ? [...regions].map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label, language)) : [], regionSelect.options[0]?.textContent || '');
        regionSelect.disabled = !showRegions;
        if (regionSelect.value) cityRecords = cityRecords.filter((record) => regionFor(record)?.id === regionSelect.value);
      }
      const cities = uniqueSorted(cityRecords.map((record) => record.city).filter(Boolean))
        .map((city) => ({ value: normalize(city), label: city }));

      rebuildSimpleSelect(citySelect, cities, cityFirst);

      const handles = [...new Set(countryRecords.flatMap((record) => record.sports))];
      const sports = handles.map((handle) => ({
        value: handle,
        label: sportLabels.get(handle) || handle
      })).sort((a, b) => a.label.localeCompare(b.label, document.documentElement.lang || 'nl'));
      rebuildSimpleSelect(sportSelect, sports, sportFirst);
      citySelect.disabled = !countryId;
      sportSelect.disabled = !countryId;
      updateSelectionPreview();
    };

    const rebuildAudienceSelect = () => {
      if (!audienceSelect) return;
      const firstLabel = audienceSelect.options?.[0]?.textContent || '';
      const current = audienceSelect.value;
      const options = new Map();
      gyms.forEach((gym) => gym.audiences.forEach((item) => {
        if (!options.has(item.handle)) options.set(item.handle, item.label);
      }));
      rebuildSimpleSelect(
        audienceSelect,
        [...options.entries()].map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label, document.documentElement.lang || 'nl')),
        firstLabel
      );
      audienceSelect.value = options.has(current) ? current : '';
    };

    const selectionKey = () => countrySelect?.value || '';

    const updateSelectionPreview = () => {
      const records = selectedIndexRecords();
      const hasCountry = Boolean(countrySelect?.value);
      updateFilterVisibility();
      if (resultCount && !loadedSelectionKey) {
        resultCount.textContent = hasCountry
          ? [countFormatter.format(records.length), labels.available].filter(Boolean).join(' ')
          : labels.selectCountry;
      }
      syncCountryChips();
      updateUrl();
    };

    const clearLoadedResults = () => {
      selectionRequestId += 1;
      gyms = [];
      loadedSelectionKey = '';
      currentPage = 1;
      resultsGrid?.replaceChildren();
      if (searchInput) {
        searchInput.value = '';
        searchInput.disabled = true;
      }
      setSearchVisibility(false);
      if (audienceSelect) {
        audienceSelect.value = '';
        audienceSelect.disabled = true;
      }
      if (emptyElement) emptyElement.hidden = true;
      if (pagination) { pagination.hidden = true; pagination.replaceChildren(); }
    };

    const hydrateSelection = async () => {
      const records = countryIndexRecords();
      if (!records.length) return;
      const key = selectionKey();
      const requestId = ++selectionRequestId;
      loadError = false;
      if (selectionCache.has(key)) {
        if (requestId !== selectionRequestId || countrySelect?.value !== key) return;
        gyms = selectionCache.get(key).slice();
        loadedSelectionKey = key;
        rebuildAudienceSelect();
        if (searchInput) searchInput.disabled = false;
        if (audienceSelect) audienceSelect.disabled = false;
        renderArchive();
        return;
      }
      if (loadingElement) {
        loadingElement.hidden = false;
        loadingElement.textContent = labels.loadingSelection || labels.loading;
      }
      if (errorElement) errorElement.hidden = true;
      try {
        const recordById = new Map(records.map((record) => [record.id, record]));
        const batches = chunk(records.map((record) => record.id), 250);
        const pages = [];
        for (let index = 0; index < batches.length; index += 3) {
          const group = batches.slice(index, index + 3);
          const results = await Promise.all(group.map((ids) => requestQuery(NODES_QUERY, { ids, language })));
          pages.push(...results);
        }
        if (requestId !== selectionRequestId || countrySelect?.value !== key) return;
        const mapped = pages.flatMap((page) => page.nodes || [])
          .map((node) => mapGym(node, recordById.get(node?.id)))
          .filter((gym) => gym?.id && gym.name);
        const received = new Set(mapped.map((gym) => gym.id));
        if (received.size !== records.length) throw new Error('Selected gym hydration was incomplete');
        gyms = mapped.sort((a, b) => a.name.localeCompare(b.name, document.documentElement.lang || 'nl'));
        selectionCache.set(key, gyms.slice());
        loadedSelectionKey = key;
        rebuildAudienceSelect();
        if (searchInput) searchInput.disabled = false;
        if (audienceSelect) audienceSelect.disabled = false;
        renderArchive();
      } catch (error) {
        if (requestId !== selectionRequestId) return;
        loadError = true;
        loadedSelectionKey = '';
        gyms = [];
        resultsGrid?.replaceChildren();
        if (errorElement) {
          errorElement.hidden = false;
          errorElement.textContent = labels.error;
        }
      } finally {
        if (requestId !== selectionRequestId) return;
        if (loadingElement) loadingElement.hidden = true;
        updateSelectionPreview();
      }
    };

    const applyUrlScope = async () => {
      const params = new URLSearchParams(window.location.search);
      currentPage = Math.max(1, parseInt(params.get('page'), 10) || 1);
      const countryParam = String(params.get('country') || '').toUpperCase();
      if (countryParam && countrySelect) {
        const option = [...countrySelect.options].find((item) => String(item.dataset.code || '').toUpperCase() === countryParam);
        if (option) countrySelect.value = option.value;
      }
      rebuildScopeFilters();
      if (sportSelect) sportSelect.value = params.get('sport') || '';
      sportChosen = Boolean(params.get('sport') || params.get('region') || params.get('city'));
      await loadCountryRegions();
      rebuildScopeFilters();
      if (regionSelect && regionFilterAvailable()) regionSelect.value = params.get('region') || '';
      rebuildScopeFilters();
      if (citySelect) citySelect.value = normalize(params.get('city') || '');
      rebuildScopeFilters();
      if (searchInput) searchInput.value = params.get('q') || '';
      updateSelectionPreview();
      return Boolean(countrySelect?.value && countryParam);
    };

    searchInput?.addEventListener('input', () => {
      currentPage = 1;
      renderArchive();
    });
    countrySelect?.addEventListener('change', () => {
      sportChosen = false;
      expanded.country = countrySelect.value === '__all__';
      countryMore.setAttribute('aria-expanded', String(expanded.country));
      countryMore.textContent = expanded.country ? root.dataset.lessCountries : root.dataset.moreCountries;
      regionRequestId += 1;
      if (regionSelect) regionSelect.value = '';
      if (sportSelect) sportSelect.value = '';
      if (citySelect) citySelect.value = '';
      clearLoadedResults();
      rebuildScopeFilters();
      if (ready && countrySelect.value && countryIndexRecords().length) {
        hydrateSelection();
        loadCountryRegions();
      }
    });
    regionSelect?.addEventListener('change', () => {
      citySelect.value = '';
      currentPage = 1;
      rebuildScopeFilters();
      if (loadedSelectionKey) renderArchive();
    });
    citySelect?.addEventListener('change', () => {
      currentPage = 1;
      rebuildScopeFilters();
      if (loadedSelectionKey) renderArchive();
    });
    sportSelect?.addEventListener('change', () => {
      sportChosen = true;
      if (regionSelect) regionSelect.value = '';
      citySelect.value = '';
      currentPage = 1;
      rebuildScopeFilters();
      if (loadedSelectionKey) renderArchive();
    });
    audienceSelect?.addEventListener('change', () => {
      currentPage = 1;
      renderArchive();
    });

    const start = async () => {
      try {
        if (loadingElement) {
          loadingElement.hidden = false;
          loadingElement.textContent = labels.loading;
        }
        if (errorElement) errorElement.hidden = true;
        if (searchInput) searchInput.disabled = true;
        if (citySelect) citySelect.disabled = true;
        if (sportSelect) sportSelect.disabled = true;
        if (audienceSelect) audienceSelect.disabled = true;
        const [records, countries, sports] = await Promise.all([
          loadIndex(),
          loadLabels('country'),
          loadLabels('sport')
        ]);
        indexRecords = records;
        countryLabels = new Map(countries.map((country) => [country.id, {
          name: String(country.name?.value || country.handle || '').trim(),
          code: String(country.code?.value || '').trim().toUpperCase()
        }]));
        sportLabels = new Map(sports.map((sport) => [
          String(sport.handle || '').trim(),
          String(sport.name?.value || sport.handle || '').trim()
        ]));
        rebuildCountrySelect();
        ready = true;
        const autoLoad = await applyUrlScope();
        if (autoLoad && selectedIndexRecords().length) await hydrateSelection();
      } catch (error) {
        loadError = true;
        if (errorElement) {
          errorElement.hidden = false;
          errorElement.textContent = labels.error;
        }
      } finally {
        if (loadingElement) loadingElement.hidden = true;
        updateSelectionPreview();
      }
    };

    start();
  });
})();























